import { CartItem } from '@givebacks/api/store';
import { Badge, ButtonMantine, Divider, Grid, InfoListCard, Stack, Text, centsToDollar } from 'ui';

import { Printty } from '../../../../packages/Printty/printty';
import { cartItemDisplayName } from '../../../utils';
import { toItemDetailString } from './Order.utils';

/**
 * Props for the Item component that renders an individual cart item within an order
 */
interface ItemProps {
  /** The cart item data to display */
  cartItem: CartItem;
  /** Whether this is the last item in the order (controls divider display) */
  isLastItem: boolean;
  /** Whether the order has been partially refunded (controls refunded badge display) */
  isPartiallyRefunded: boolean;
  /** Whether the current user has permission to modify cart items (controls fulfillment button display) */
  hasCartWriteAccess: boolean;
  /**
   * Callback function triggered when the fulfillment status of an item is toggled
   * @param cartItemUuid - The UUID of the cart item being modified
   * @param fulfilled - The current fulfillment status (will be toggled)
   */
  onFulfill?: (cartItemUuid: string | undefined, fulfilled: boolean) => void;
}

const Item = ({
  cartItem,
  isLastItem,
  isPartiallyRefunded,
  hasCartWriteAccess,
  onFulfill,
}: ItemProps) => {
  return (
    <Stack px="md" gap="xs">
      <Grid gutter={2}>
        <Grid.Col span={4}>
          <Stack gap="xs">
            <Text type="caption" c="gray">
              {cartItemDisplayName(cartItem)}
            </Text>
            {!!cartItem?.fulfillment?.giveback && (
              <Badge
                type="success"
                styles={{
                  label: {
                    textTransform: 'none',
                  },
                }}
              >
                Your cause gets {centsToDollar(Number(cartItem?.fulfillment?.giveback))}
              </Badge>
            )}
          </Stack>
        </Grid.Col>
        <Grid.Col span={2}>
          <Text type="caption" c="gray">
            {cartItem?.price ? centsToDollar(cartItem.price) : '--'}
          </Text>
        </Grid.Col>
        <Grid.Col span={2}>
          <Text type="caption" c="gray">
            {cartItem.quantity}
          </Text>
        </Grid.Col>
        <Grid.Col span="auto">
          <Text type="caption" c="gray">
            {cartItem?.total_to_cause ? centsToDollar(cartItem.total_to_cause) : '--'}
          </Text>
        </Grid.Col>
        <Grid.Col span="auto" mt={-6}>
          <Stack gap="xs">
            {cartItem?.reversed_at && isPartiallyRefunded && (
              <Badge variant="filled" type="warning">
                Refunded
              </Badge>
            )}

            {hasCartWriteAccess && cartItem.store_item?.fulfillable && (
              <Printty.NoPrintTarget>
                <ButtonMantine
                  variant="subtle"
                  size="xs"
                  color="primary"
                  onClick={() => onFulfill?.(cartItem.uuid, cartItem.fulfilled)}
                >
                  {cartItem.fulfilled ? 'Mark Un-Fulfilled' : 'Mark Fulfilled'}
                </ButtonMantine>
              </Printty.NoPrintTarget>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      {Object.keys(cartItem?.options).length > 0 && (
        <InfoListCard
          header={<InfoListCard.Header title="Item Details" />}
          cols={1}
          direction="column"
        >
          {Object.keys(cartItem?.options).map((key) => (
            <InfoListCard.Item
              key={key}
              label={`${key}:`}
              detail={toItemDetailString(cartItem?.options?.[key])}
            />
          ))}
        </InfoListCard>
      )}

      {!isLastItem && <Divider size="sm" color="gray.0" />}
    </Stack>
  );
};

export { Item };
